import { useCallback, useEffect, useRef, useState } from 'react';

import { useResetOnWindowBlur } from './hooks/useResetOnWindowBlur';

// ─────────────────────────────────────────────────────────────────────────────

export const useCustomizationStepperTimeout = (params: Params = {}) => {
  const { resetOnWindowBlur = false } = params;

  //
  // ─── State ───────────────────────────────────────────────────────────

  const [activeStepperId, setActiveStepperId] = useState<string | null>(null);

  // ─── Refs ────────────────────────────────────────────────────────────

  const timeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const clearStepperTimeout = useCallback(() => {
    if (!timeout.current) return;

    clearTimeout(timeout.current);
  }, []);

  const resetStepperTimeout = useCallback(() => {
    clearStepperTimeout();

    timeout.current = setTimeout(() => {
      setActiveStepperId(null);
    }, STEPPER_TIMEOUT);
  }, [clearStepperTimeout]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => clearStepperTimeout, [clearStepperTimeout]);

  useResetOnWindowBlur({
    skip: !activeStepperId || !resetOnWindowBlur,
    onReset: resetStepperTimeout,
  });

  // ─────────────────────────────────────────────────────────────────────

  return {
    activeStepperId,
    setActiveStepperId,
    resetStepperTimeout,
    clearStepperTimeout,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const STEPPER_TIMEOUT = 1800;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  resetOnWindowBlur?: boolean;
};
